/**
 * Palette navigation targets, DERIVED from the help surface registry (OPSFL-POLISH-1).
 *
 * There is deliberately no hand-written list of surfaces here: `help/surfaceRegistry.ts` is the
 * closed contract list, and its content docs already carry the human title and one-liner the
 * operator should read. A new surface therefore appears in the palette the moment it ships its
 * registry row + doc — it can never silently go missing from navigation.
 *
 * A surface is reachable from the palette only when it has a PARAMETER-FREE route: `entity-detail`
 * (`/entities/:entityId`) has none, so it is not a "Go to" row — you reach it by searching the
 * entity itself, which is the honest affordance.
 */
import { getSurfaceDoc } from '@/help/contentLoader';
import { SURFACES } from '@/help/surfaceRegistry';
import type { PaletteItem } from './paletteModel';

function titleCase(surfaceId: string): string {
  return surfaceId
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** The first route that can be navigated to with no parameters, or null. */
export function staticRoute(routes: readonly string[]): string | null {
  return routes.find((r) => !r.includes(':')) ?? null;
}

/** "Go to" rows for every registered surface that has a parameter-free route. */
export function surfaceTargets(): PaletteItem[] {
  const items: PaletteItem[] = [];
  for (const surface of SURFACES) {
    const route = staticRoute(surface.routes);
    if (!route) continue;
    const doc = getSurfaceDoc(surface.surfaceId);
    items.push({
      id: `surface:${surface.surfaceId}`,
      kind: 'surface',
      label: doc?.title || titleCase(surface.surfaceId),
      hint: doc?.oneLiner || undefined,
      keywords: `${surface.surfaceId} ${surface.routes.join(' ')} ${surface.page}`,
      route,
      meta: route,
    });
  }
  return items;
}
